import React, { Fragment, useCallback, useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LocalizationContext } from 'app/localization/Context';
import { CurrentPageContext, HistoryStackContext } from '../general-components/HistoryStackContext';

import DeviceDetail, { AudioControllerManagePage, DeviceSettingPageInner } from './device/deivce-detail/DeviceDetail';
import CreateOrEditBind from './bind/CreateBind';
import BindDetail from './bind/BindDetail';
import CreateOrEditScenario from './scenario/CreateScenario';
import { ScenarioDetail } from './scenario/pages/Overview + Detail';
import AppSettings from './setting/AppSettings';
import AddDevice from './device/add-device/AddDevice';
import HomeList from '../new-home/HomeList';
import { ScenarioReportPopup } from '../main-pages/scenarios/Scenarios';
// import MessageCenterList from './message-center/MessageCenterList';
import About, { LicensesPage, SupportPage } from './setting/About';
import Users, { GetNumberToAddUser, HomeDetailPage, UserDetailPage } from './setting/UsersAndHome';
import Rooms from './setting/Room/Rooms';
import { RoomDetail, RoomSelectDevice, RoomSelectNameAndIcon } from './setting/Room/Detail';
import AddRoom from './setting/Room/Add/AddRoom';
import { SelectMusicList, ShowMusicList } from './device/deivce-detail/device-control/audio-controller/SelectMusicList';
import { SyncSettings, SyncSettingsSelectDevice } from './device/deivce-detail/sync-settings/SyncSettings';
import { AskProfileOverlay } from '../new-home/Profile';
import { SiriRunScenario } from './scenario/siri/SiriScenario';
import { RemoteControl } from './device/deivce-detail/device-control/remote/RemoteControl';
import { OTAReportPage } from './device/deivce-detail/OTAReport';
import AddRemote from './device/deivce-detail/device-control/remote/add-remote/AddRemote';
import FilterReports from './report/FilterReports';
import MultiOTA from './setting/MultiOTA/MultiOTA';
import { DetailedReportsOverlayPage } from '../main-pages/reports/Reports';

import './OverlayPages.scss'
import AIChat from './ai/AIChat';


const FadeFromTopAnimationPageVariants = {
    initial: {
        translateY: "-2%",
        opacity: 0,
        backdropFilter: "blur(0px)",
        WebkitBackdropFilter: "blur(0px)"
    },
    animate: {
        translateY: "0%",
        opacity: 1,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)"
    },
    exit: {
        translateY: "-2%",
        opacity: 0,
        backdropFilter: "blur(0px)",
        WebkitBackdropFilter: "blur(0px)"
    }
};

const BottonToTopAnimationPageVariants = {
    initial: {
        translateY: "100%"
    },
    animate: {
        translateY: "0%"
    },
    exit: {
        translateY: "100%"
    }
};

const RightToLeftAnimationPageVariants = {
    initial: {
        translateX: "100%"
    },
    animate: { // steady state
        translateX: "0%"
    },
    exit: {
        translateX: "100%"
    }
};

const LeftToRightAnimationPageVariants = {
    initial: {
        translateX: "-100%"
    },
    animate: { // steady state
        translateX: "0%"
    },
    exit: {
        translateX: "-100%"
    }
};

function OverlayPageAnimationContainer({ variants, children }) {
    return (
        <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            transition={{
                duration: 0.5,  // overlay-animation-duration
                ease: "easeInOut"
            }}

            className="overlay-page"
        >
            {children}
        </motion.div>
    );
}

function OverlayPageAnimationSuperContainer({ inWideView, isThereAnyOverlayPage, children }) {
    if (inWideView) {
        return (
            <AnimatePresence>
                {isThereAnyOverlayPage ? <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={{
                        initial: {
                            opacity: 0
                        },
                        animate: {
                            opacity: 1
                        },
                        exit: {
                            opacity: 0
                        }
                    }}
                    transition={{
                        ease: "easeInOut",
                        duration: 0.5,
                    }}
                    key="OverlayPageAnimationSuperContainer_back"
                    className="overlay-page-super-container-blur-back"
                /> : null}
                {isThereAnyOverlayPage ? <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={{
                        initial: {
                            marginTop: "100%"
                        },
                        animate: { // steady state
                            marginTop: "0"
                        },
                        exit: {
                            marginTop: "100%"
                        }
                    }}
                    transition={{
                        duration: 0.5,  // overlay-animation-duration
                        ease: "easeInOut"
                    }}
                    key="OverlayPageAnimationSuperContainer_content"
                    className="overlay-page-super-container"
                >
                    {children}
                </motion.div> : null}
            </AnimatePresence>
        );
    } else {
        return (
            <Fragment>
                {children}
            </Fragment>
        );
    }
}

export const overlayPageAnimation = {
    "/reports/detailed-reports": "horizontal",
    "/reports/filter": "vertical",
    "/reports/filter-h": "horizontal",
    "/ask_profile": "horizontal",
    "/ota-report": "vertical",
    "/homes/switch": "vertical-fade",
    "/ai/chat": "vertical-fade",
    // "/message_center/list": "horizontal",
    "/homes/profile": "horizontal",

    "/scenarios/siri_run_scenario": "horizontal",
    "/scenarios/create_or_edit": "vertical",
    "/scenarios/detail": "horizontal",

    "/binds/create_or_edit": "vertical",
    "/binds/detail": "horizontal",

    "/devices/add": "vertical",
    "/devices/detail": "horizontal",
    "/devices/detail/settings": "horizontal",
    "/devices/detail/remote-control": "horizontal",
    "/devices/detail/remote/add": "horizontal",
    "/devices/detail/select-music-list": "horizontal",
    "/devices/detail/select-music-list/show-list": "horizontal",
    "/devices/detail/audio-controller-manage": "horizontal",
    "/devices/detail/sync_settings": "horizontal",
    "/devices/detail/sync_settings/devices": "horizontal",

    "/settings/about": "horizontal",
    "/settings/about/support": "horizontal",
    "/settings/about/licenses": "horizontal",
    "/settings/user_management": "horizontal",
    "/settings/user_management/detail": "horizontal",
    "/settings/user_management/add": "horizontal",
    // "/settings/edit_profile": "horizontal",
    "/settings/app_settings": "horizontal",

    "/settings/multi_ota": "horizontal",

    "/settings/rooms_management": "horizontal",
    "/settings/rooms_management/add": "vertical",
    "/settings/rooms_management/detail": "horizontal",
    "/settings/rooms_management/detail/edit_devices": "horizontal",
    "/settings/rooms_management/detail/settings": "horizontal",
}

function OverlayPages({ inWideView, homesManager, gateway, profile, swController }) {
    const hs = useContext(HistoryStackContext);
    const l = useContext(LocalizationContext);

    const createPage = useCallback((path, isFirstOverlayPage) => {
        let variants = undefined;
        switch (overlayPageAnimation[path]) {
            case "horizontal":
                variants = l.isRTL ?
                    LeftToRightAnimationPageVariants :
                    RightToLeftAnimationPageVariants;
                break;
            case "vertical-fade":
                variants = FadeFromTopAnimationPageVariants;
                break;
            case "vertical":
            default:
                variants = BottonToTopAnimationPageVariants;
                break;
        }

        // no animation for the bottom page in the stack
        // because we have apopup animation inWideView
        if (inWideView && isFirstOverlayPage) {
            variants = undefined;
        }

        switch (path) {
            case "/reports/filter":
            case "/reports/filter-h":
                return (
                    <OverlayPageAnimationContainer key="page_reports_filter"
                        variants={variants}>
                        <FilterReports gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/reports/detailed-reports":
                return (
                    <OverlayPageAnimationContainer key="page_reports_detailed_reports"
                        variants={variants}>
                        <DetailedReportsOverlayPage gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/ask_profile":
                return (
                    <OverlayPageAnimationContainer key="page_ask_profile"
                        variants={variants}>
                        <AskProfileOverlay profile={profile} />
                    </OverlayPageAnimationContainer>
                );
            case "/ota-report":
                return (
                    <OverlayPageAnimationContainer key="page_ota_report"
                        variants={variants}>
                        <OTAReportPage 
                            otaProgressController={gateway.otaProgressController}
                            gwUpdateInfo={gateway.gwUpdateInfo} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail":
                return (
                    <OverlayPageAnimationContainer key="page_device_detail"
                        variants={variants}>
                        <DeviceDetail gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/settings":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_settings"
                        variants={variants}>
                        <DeviceSettingPageInner
                            devices={gateway.devices}
                            negotiatedProtocol={gateway.negotiatedProtocol}
                            otaProgressController={gateway.otaProgressController} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/remote-control":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_remote_control"
                        variants={variants}>
                        <RemoteControl
                            devices={gateway.devices}
                            currentUser={gateway.currentUser}
                            negotiatedProtocol={gateway.negotiatedProtocol} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/remote/add":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_remote_add"
                        variants={variants}>
                        <AddRemote
                            addRemoteController={gateway.addRemoteController}
                            devices={gateway.devices} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/sync_settings":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_sync_state"
                        variants={variants}>
                        <SyncSettings
                            setDeviceFolowers={gateway.setDeviceFolowers}
                            devices={gateway.devices} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/sync_settings/devices":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_sync_state_devices"
                        variants={variants}>
                        <SyncSettingsSelectDevice
                            setDeviceFolowers={gateway.setDeviceFolowers}
                            devices={gateway.devices} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/audio-controller-manage":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_audio_controller_manage"
                        variants={variants}>
                        <AudioControllerManagePage
                            currentUser={gateway.currentUser}
                            devices={gateway.devices}
                            otaProgressController={gateway.otaProgressController} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/select-music-list":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_select_music_list"
                        variants={variants}>
                        <SelectMusicList
                            audioFilesData={gateway.audioFilesData}
                            audioFilesController={gateway.audioFilesController} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/detail/select-music-list/show-list":
                return (
                    <OverlayPageAnimationContainer
                        key="page_devices_detail_select_music_list_show_list"
                        variants={variants}>
                        <ShowMusicList
                            devices={gateway.devices}
                            audioFilesData={gateway.audioFilesData}
                            audioFilesController={gateway.audioFilesController} />
                    </OverlayPageAnimationContainer>
                );
            case "/devices/add":
                return (
                    <OverlayPageAnimationContainer key="page_device_add"
                        variants={variants}>
                        <AddDevice gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/scenarios/siri_run_scenario":
                return (
                    <OverlayPageAnimationContainer key="page_siri_run_scenario"
                        variants={variants}>
                        <SiriRunScenario gateway={gateway} home={homesManager.activeHome} />
                    </OverlayPageAnimationContainer>
                );
            case "/scenarios/create_or_edit":
                return (
                    <OverlayPageAnimationContainer key="page_create_scenario"
                        variants={variants}>
                        <CreateOrEditScenario gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/scenarios/detail":
                return (
                    <OverlayPageAnimationContainer key="page_scenario_detail"
                        variants={variants}>
                        <ScenarioDetail gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/binds/create_or_edit":
                return (
                    <OverlayPageAnimationContainer key="page_create_bind"
                        variants={variants}>
                        <CreateOrEditBind gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/binds/detail":
                return (
                    <OverlayPageAnimationContainer key="page_bind_detail"
                        variants={variants}>
                        <BindDetail gateway={gateway} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/app_settings":
                return (
                    <OverlayPageAnimationContainer key="page_settings_app_settings"
                        variants={variants}>
                        <AppSettings
                            swController={swController}
                            showSecondsInLog={gateway.showSecondsInLog}
                            setShowSecondsInLog={gateway.setShowSecondsInLog}
                            showDeleteAllDevicesButton={gateway.showDeleteAllDevicesButton}
                            setShowDeleteAllDevicesButton={gateway.setShowDeleteAllDevicesButton}
                        />
                    </OverlayPageAnimationContainer>
                );
            case "/ai/chat":
                return (
                    <OverlayPageAnimationContainer key="page_ai_chat"
                        variants={variants}>
                        <AIChat
                            aiChat={gateway.aiChat}
                            showBackground={inWideView} blurBackground={!inWideView} />
                    </OverlayPageAnimationContainer>
                );
            case "/homes/switch":
                return (
                    <OverlayPageAnimationContainer key="page_homes_switch"
                        variants={variants}>
                        <HomeList
                            homes={homesManager.homes} activeHome={homesManager.activeHome}
                            reloadSoloHomes={homesManager.reloadSoloHomes}
                            isSoloLoggedIn={homesManager.isSoloLoggedIn}
                            logoutFromSolo={homesManager.logoutFromSolo}
                            showBackground={inWideView} blurBackground={!inWideView}
                            onAddHomeClick={homesManager.startAddHomeProcess} />
                    </OverlayPageAnimationContainer>
                );
            // case "/message_center/list":
            //     return (
            //         <OverlayPageAnimationContainer key="page_message_center_list"
            //             variants={variants}>
            //             <MessageCenterList />
            //         </OverlayPageAnimationContainer>
            //     );
            case "/settings/about":
                return (
                    <OverlayPageAnimationContainer key="page_settings_about"
                        variants={variants}>
                        <About gateway={gateway} swController={swController} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/about/support":
                return (
                    <OverlayPageAnimationContainer key="page_settings_about_support"
                        variants={variants}>
                        <SupportPage />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/about/licenses":
                return (
                    <OverlayPageAnimationContainer key="page_settings_about_licenses"
                        variants={variants}>
                        <LicensesPage />
                    </OverlayPageAnimationContainer>
                );
            case "/homes/profile":
                return (
                    <OverlayPageAnimationContainer key="page_settings_user_management"
                        variants={variants}>
                        <HomeDetailPage
                            home={homesManager.activeHome}
                            currentUser={gateway.currentUser}
                            sendChangeProfile={gateway.changeSoloHomeProfile} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/user_management":
                return (
                    <OverlayPageAnimationContainer key="page_settings_user_management"
                        variants={variants}>
                        <Users gateway={gateway} profile={profile} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/user_management/detail":
                return (
                    <OverlayPageAnimationContainer key="page_settings_user_management"
                        variants={variants}>
                        <UserDetailPage
                            allUsers={gateway.allUsers}
                            currentUser={gateway.currentUser}
                            profile={profile} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/user_management/add":
                return (
                    <OverlayPageAnimationContainer key="page_settings_user_management_add"
                        variants={variants}>
                        <GetNumberToAddUser addUser={gateway.addUser} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/multi_ota":
                return (
                    <OverlayPageAnimationContainer key="page_settings_multi_ota"
                        variants={variants}>
                        <MultiOTA
                            devices={gateway.devices}
                            currentUser={gateway.currentUser}
                            otaProgressController={gateway.otaProgressController} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/rooms_management":
                return (
                    <OverlayPageAnimationContainer key="page_settings_rooms_management"
                        variants={variants}>
                        <Rooms
                            dataFetched={gateway.dataFetched}
                            roomsData={gateway.roomsData}
                            roomsController={gateway.roomsController}
                            currentUser={gateway.currentUser}
                            devices={gateway.devices} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/rooms_management/detail":
                return (
                    <OverlayPageAnimationContainer
                        key="page_settings_rooms_management_detail"
                        variants={variants}>
                        <RoomDetail
                            roomsData={gateway.roomsData}
                            currentUser={gateway.currentUser}
                            devices={gateway.devices}
                            roomsController={gateway.roomsController} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/rooms_management/detail/edit_devices":
                return (
                    <OverlayPageAnimationContainer
                        key="page_settings_rooms_management_detail_edit_devices"
                        variants={variants}>
                        <RoomSelectDevice
                            createRoomMode={false}
                            devices={gateway.devices}
                            roomsData={gateway.roomsData}
                            roomsController={gateway.roomsController} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/rooms_management/detail/settings":
                return (
                    <OverlayPageAnimationContainer
                        key="page_settings_rooms_management_detail_settings"
                        variants={variants}>
                        <RoomSelectNameAndIcon
                            createRoomMode={false}
                            roomsData={gateway.roomsData}
                            roomsController={gateway.roomsController} />
                    </OverlayPageAnimationContainer>
                );
            case "/settings/rooms_management/add":
                return (
                    <OverlayPageAnimationContainer
                        key="page_settings_rooms_management_add"
                        variants={variants}>
                        <AddRoom
                            devices={gateway.devices}
                            roomsController={gateway.roomsController} />
                    </OverlayPageAnimationContainer>
                );
            default:
                return null;
        }
    }, [homesManager, gateway, profile, l.isRTL, swController, inWideView]);

    // const inner = useMemo(() => {
    //     return createPage(hs.pagePath)
    // }, [o, createPage]);

    const showAskProfile =
        (gateway.dataFetched) &&
        ((gateway.currentUser.name === undefined) ||
            (gateway.currentUser.name === null) ||
            (gateway.currentUser.name === "") ||
            (gateway.currentUser.language === undefined) ||
            (gateway.currentUser.language === null) ||
            (gateway.currentUser.language === "") ||
            (gateway.currentUser.avatar === undefined) ||
            (gateway.currentUser.avatar === null) ||
            (gateway.currentUser.avatar === 0));

    const stackPages = useMemo(() => {
        const pages = [];
        let pageStack = [...hs.pageStack];
        if (showAskProfile) {
            pageStack.unshift({ path: "/ask_profile", pageUUID: "id_of_ask_profile", state: {}, isOverlayPage: true });
        }
        let isFirstOverlayPage = true;
        for (let i = 0; i < pageStack.length; i++) {
            const page = pageStack[i];
            if (page.isOverlayPage) {
                pages.push(
                    <CurrentPageContext.Provider
                        key={"history_stack_page_key_" + String(page.pageUUID)}
                        value={{
                            path: page.path,
                            state: page.innerState,
                            id: page.pageUUID,
                            dismiss: () => { hs.checkAndBack({ pageUUID: page.pageUUID }) },
                            dontUseBackToDissmiss: inWideView && isFirstOverlayPage,
                            inOverlayPage: true,
                        }}>
                        {createPage(page.path, isFirstOverlayPage)}
                    </CurrentPageContext.Provider>
                );
                isFirstOverlayPage = false;
            }
        }
        return pages;
    }, [hs, createPage, inWideView, showAskProfile]);

    const isThereAnyOverlayPage = (stackPages.length > 0);

    return (
        <Fragment>
            <AnimatePresence>
                <OverlayPageAnimationSuperContainer
                    inWideView={inWideView}
                    isThereAnyOverlayPage={isThereAnyOverlayPage}
                    key="over_page_super_container"
                >
                    <AnimatePresence>
                        {stackPages}
                    </AnimatePresence>
                </OverlayPageAnimationSuperContainer>
            </AnimatePresence>

            <ScenarioReportPopup scenarioReports={gateway.scenarioReports} devices={gateway.devices} />
        </Fragment>
    )
}

export default OverlayPages;